import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Heart } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// -----------------------------------------------------------------------------
// SaveCoachButton
// -----------------------------------------------------------------------------
// A tiny heart-toggle that reads/writes to /api/favorites.
// The client identity is now the Google-verified email from AuthContext.
// Unauthenticated visitors get a "Sign in with Google" modal.
// -----------------------------------------------------------------------------
export default function SaveCoachButton({
  coachSlug,
  variant = "icon",        // "icon" (card overlay) | "pill" (profile button)
  onChange,
  className = "",
}) {
  const { user, openSignIn } = useAuth();
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user?.email) { setSaved(false); return; }
    let cancelled = false;
    axios.get(`${API}/favorites`, { params: { email: user.email } })
      .then((r) => { if (!cancelled) setSaved((r.data.favorites || []).includes(coachSlug)); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [coachSlug, user?.email]);

  const persist = async () => {
    setBusy(true);
    try {
      if (saved) {
        await axios.delete(`${API}/favorites/${coachSlug}`, { params: { email: user.email } });
        setSaved(false); onChange?.(false);
        toast("Removed from saved.", { icon: "✓" });
      } else {
        await axios.post(`${API}/favorites`, { client_email: user.email, coach_slug: coachSlug });
        setSaved(true); onChange?.(true);
        toast.success("Saved for later.");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't save this coach.";
      toast.error(typeof msg === "string" ? msg : "Couldn't save this coach.");
    } finally {
      setBusy(false);
    }
  };

  const click = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!user) return openSignIn("Sign in to save this coach to your shortlist.");
    persist();
  };

  const styleFor = () => {
    if (variant === "pill") {
      return `inline-flex items-center gap-2 h-11 px-4 rounded-full border text-[13px] font-semibold transition-colors ${
        saved
          ? "bg-[#F43F5E]/10 border-[#F43F5E]/40 text-[#E11D48]"
          : "bg-white border-slate-200 text-slate-700 hover:border-slate-300"
      } ${busy ? "opacity-60 cursor-not-allowed" : ""}`;
    }
    return `absolute top-3 right-3 z-[2] w-9 h-9 rounded-full grid place-items-center transition-colors backdrop-blur ${
      saved
        ? "bg-[#F43F5E] text-white"
        : "bg-white/85 text-slate-500 hover:text-[#E11D48]"
    }`;
  };

  return (
    <button
      type="button"
      onClick={click}
      disabled={busy}
      aria-pressed={saved}
      aria-label={saved ? "Unsave coach" : "Save coach"}
      data-testid={`save-coach-${coachSlug}`}
      className={`${styleFor()} ${className}`}
    >
      <Heart
        className={variant === "pill" ? "w-4 h-4" : "w-4 h-4"}
        strokeWidth={2}
        fill={saved ? "currentColor" : "none"}
      />
      {variant === "pill" && <span>{saved ? "Saved" : "Save coach"}</span>}
    </button>
  );
}
